import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { fieldCls, labelCls } from "@/components/admin/BrandFormDialog";
import { CATEGORIES, errMsg, formatSize, http } from "@/lib/api";

const today = () => new Date().toISOString().slice(0, 10);
export const warnDuplicates = (data) => {
  const dups = Array.isArray(data?.duplicates) ? data.duplicates : [];
  if (dups.length) toast.warning("Possible duplicate file", { description: `The same file is already used by: ${dups.join("; ")}` });
};

const FileField = ({ id, label, accept, current, onFile, removed, onRemove, testId }) => (
  <div className="rounded-2xl border border-ph-line p-4">
    <label className={labelCls} htmlFor={id}>{label}</label>
    {current && (
      <div className="mt-2 flex items-center justify-between gap-2 text-xs text-ph-muted">
        <span className="truncate">Current: {current.filename} ({formatSize(current.size)})</span>
        <label className="flex shrink-0 items-center gap-1.5"><Checkbox checked={removed} onCheckedChange={onRemove} data-testid={`${testId}-remove`} /> Remove</label>
      </div>
    )}
    <input id={id} type="file" accept={accept} onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid={testId}
      className={`${fieldCls} file:mr-3 file:rounded-full file:border-0 file:bg-ph-raised file:px-3 file:py-1`} />
    {current && <p className="mt-1.5 text-xs text-ph-muted">Choose a new file to replace the current one.</p>}
  </div>
);

export function DocFormDialog({ open, onOpenChange, doc, brands, defaultBrandId, onSaved }) {
  const [form, setForm] = useState({});
  const [pdf, setPdf] = useState(null);
  const [excel, setExcel] = useState(null);
  const [rm, setRm] = useState({ pdf: false, excel: false });
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({ brand_id: doc?.brand_id || defaultBrandId || "", title: doc?.title || "", category: doc?.category || "", version: doc?.version || "", updated_date: doc?.updated_date || today() });
    setPdf(null); setExcel(null); setRm({ pdf: false, excel: false }); setProgress(0);
  }, [open, doc, defaultBrandId]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.brand_id || !form.category) { toast.error("Choose a brand and category"); return; }
    if (!doc && !pdf && !excel) { toast.error("Attach a PDF or Excel file"); return; }
    const fd = new FormData();
    ["title", "category", "version", "updated_date"].forEach((k) => fd.append(k, form[k]));
    if (pdf) fd.append("pdf", pdf);
    if (excel) fd.append("excel", excel);
    setBusy(true);
    try {
      const cfg = { onUploadProgress: (p) => setProgress(p.total ? Math.round((p.loaded / p.total) * 100) : 0) };
      if (doc) {
        fd.append("remove_pdf", rm.pdf);
        fd.append("remove_excel", rm.excel);
        const { data } = await http.put(`/admin/documents/${doc.id}`, fd, cfg);
        warnDuplicates(data);
      } else {
        fd.append("brand_id", form.brand_id);
        const { data } = await http.post("/admin/documents", fd, cfg);
        warnDuplicates(data);
      }
      toast.success(doc ? "Price list updated" : "Saved as draft", { description: doc ? form.title : `${form.title} is not public yet. Press Publish in the list when it's ready.` });
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto rounded-3xl" data-testid="doc-form-dialog" data-lenis-prevent>
        <DialogHeader><DialogTitle className="font-display text-2xl font-extrabold text-ph-ink">{doc ? "Edit / replace price list" : "Upload price list"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <p className={labelCls}>Brand</p>
            <Select value={form.brand_id || undefined} onValueChange={set("brand_id")} disabled={!!doc}>
              <SelectTrigger className="mt-1.5 h-12 rounded-2xl" data-testid="doc-form-brand"><SelectValue placeholder="Select brand" /></SelectTrigger>
              <SelectContent>{brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><label className={labelCls} htmlFor="doc-title">Price list name</label>
            <input id="doc-title" required value={form.title || ""} onChange={(e) => set("title")(e.target.value)} placeholder="e.g. LV Switchgear Price List" className={fieldCls} data-testid="doc-form-title" /></div>
          <div>
            <p className={labelCls}>Category</p>
            <Select value={form.category || undefined} onValueChange={set("category")}>
              <SelectTrigger className="mt-1.5 h-12 rounded-2xl" data-testid="doc-form-category"><SelectValue placeholder="Select category" /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className={labelCls} htmlFor="doc-version">Revision / version</label>
              <input id="doc-version" value={form.version || ""} onChange={(e) => set("version")(e.target.value)} placeholder="e.g. 2026-04" className={fieldCls} data-testid="doc-form-version" /></div>
            <div><label className={labelCls} htmlFor="doc-date">Effective date</label>
              <input id="doc-date" type="date" required value={form.updated_date || ""} onChange={(e) => set("updated_date")(e.target.value)} className={fieldCls} data-testid="doc-form-date" /></div>
          </div>
          <FileField id="doc-pdf" label="PDF file (max 25 MB)" accept=".pdf,application/pdf" current={doc?.pdf} onFile={setPdf} removed={rm.pdf} onRemove={(v) => setRm({ ...rm, pdf: !!v })} testId="doc-form-pdf" />
          <FileField id="doc-excel" label="Excel file — .xlsx or .xls (max 25 MB)" accept=".xlsx,.xls" current={doc?.excel} onFile={setExcel} removed={rm.excel} onRemove={(v) => setRm({ ...rm, excel: !!v })} testId="doc-form-excel" />
          {busy && <Progress value={progress} className="h-2" />}
          <button type="submit" disabled={busy} data-testid="doc-form-submit" className="flex h-12 w-full items-center justify-center gap-2 rounded-full bg-ph-lime font-semibold text-ph-navy hover:shadow-[0_0_24px_rgba(182,255,92,0.45)] disabled:opacity-60">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {doc ? "Save changes" : "Publish price list"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
